<template>
	<BaseLoading
		v-if="isLoadingGetSingle"
	/>
	<CForm
		v-else
		class="main-wrapper form-standard"
	>
		<CRow class="d-flex align-items-center mb-2">
			<CCol>
				<p class="typo-h4">
					Channel info
				</p>
			</CCol>
			<CCol class="text-right">
				<p class="typo-h4 typo-helper-text text-black-45">
					Slug ID:
					<b>
						Generate after saves
					</b>
				</p>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="$v.form.name.$model"
					:label="`${$t('message.channel_name')}*`"
					:is-valid="formState.name.valid"
					:invalid-feedback="formState.name.error"
					:description="formHelper.name"
					:disabled="isUser"
					readonly
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<BaseDropDown
					v-model="$v.form.type.$model"
					:options="CHANNEL_TYPE_OPTIONS"
					:label-drop-down="`${$t('message.channel_type')}*`"
					:placeholder="$t('message.selection')"
					:searchable="false"
					:is-valid="formState.type.valid"
					:invalid-feedback="formState.type.error"
					:disabled="isUser"
					label="name"
					track-by="value"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<CInput
					v-model="$v.form.link.$model"
					:label="$t('message.link_to_channel')"
					:is-valid="formState.link.valid"
					:invalid-feedback="formState.link.error"
					:description="formHelper.link"
					:disabled="isUser"
				/>
			</CCol>
		</CRow>
		<CRow>
			<CCol lg="12">
				<CTextarea
					v-model="form.remark"
					:label="$t('message.remark')"
					:disabled="isUser"
					rows="3"
				/>
			</CCol>
		</CRow>
		<CRow class="container-date">
			<CCol lg="12">
				<p>
					{{ $t('message.date_created', { date: data.createdAt }) }}
				</p>
				<p>
					{{ $t('message.date_modified', { date: data.updatedAt }) }}
				</p>
			</CCol>
		</CRow>
		<BaseActionSubmit
			v-if="isAdmin"
			:is-loading="isLoadingchannelUpdate"
			:submit-text="$t('button.save_change')"
			:cancel-text="$t('button.cancel')"
			@onSubmit="handleSubmit"
			@onCancel="handleCancel"
			@onRemove="handleOpenModalRemove"
		/>
		<!-- Modal Confirm Delete -->
		<ModalConfirmDelete
			ref="modal-confirm-delete"
			:show.sync="isShowModalConfirmDelete"
			:title="$t('message.do_you_want_to_remove_this')"
			:disabled-submit="isDeleting"
			@onSubmit="handleConfirmRemove"
			@onCancel="handleCancelRemove"
		/>
		<!-- End Modal Confirm Delete -->
	</CForm>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { required, maxLength, url } from 'vuelidate/lib/validators';
import { inputStateValid, inputStateError } from '../helpers/vuelidator';
import { getSelectedValue } from '../helpers/dropdown';
import { CHANNEL_TYPE, CHANNEL_TYPE_OPTIONS } from '../enums/channel';
import { ROUTER_NAME } from '../enums/router';

export default {
	name: 'ChannelUpdate',

	data() {
		return {
			CHANNEL_TYPE_OPTIONS,
			form: {
				name: null,
				link: null,
				remark: null,
				type: getSelectedValue(CHANNEL_TYPE.ONLINE, CHANNEL_TYPE_OPTIONS), // default as `online`
			},
			isShowModalConfirmDelete: false,
		};
	},

	validations: {
		form: {
			name: {
				required,
				maxLength: maxLength(255),
			},
			type: {
				required,
			},
			link: {
				url,
			},
		},
	},

	computed: {
		...mapState('channelSingle', {
			data: 'data',
			getSingleError: 'error',
			isLoadingGetSingle: 'isLoading',
		}),
		...mapState('channelUpdate', {
			errorChannelUpdate: 'error',
			isLoadingchannelUpdate: 'isLoading',
		}),
		...mapGetters('me', {
			isUser: 'isUser',
			isAdmin: 'isAdmin',
		}),
		...mapState('channelDelete', {
			isDeleting: 'isLoading',
			errorDelete: 'error',
		}),
		formState() {
			return {
				name: {
					valid: inputStateValid(this.$v.form.name),
					error: inputStateError(this.$v.form.name)(this),
				},
				type: {
					valid: inputStateValid(this.$v.form.type),
					error: inputStateError(this.$v.form.type)(this),
				},
				link: {
					valid: inputStateValid(this.$v.form.link),
					error: inputStateError(this.$v.form.link)(this),
				},
			};
		},
		formHelper() {
			// use IIFE becausse it's easy to use and more clearly in template
			// these properties can be null or string not an arrow function
			return {
				name: (() => {
					const isErrorMessageDisplay = this.formState.name.error !== null;
					return !isErrorMessageDisplay
						? this.$t('message.maximum_characters', { number: 255 })
						: null;
				})(),
				link: (() => {
					const isErrorMessageDisplay = this.formState.link.error !== null;
					return !isErrorMessageDisplay
						? this.$t('message.optional')
						: null;
				})(),
			};
		},
	},

	async created() {
		await this.fetchData();

		// set state form
		this.setState();

		// set page title
		this.setPageTitle({
			title: this.data.name,
			isShow: true,
		});
	},

	methods: {
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		...mapActions('channelSingle', {
			getChannel: 'getChannel',
		}),
		...mapActions('channelUpdate', {
			updateChannel: 'updateChannel',
		}),
		...mapActions('channelDelete', {
			removeChannel: 'removeChannel',
		}),
		fetchData() {
			return this.getChannel(this.$route.params.id);
		},
		setState() {
			this.form.name = this.data.name;
			this.form.link = this.data.url;
			this.form.remark = this.data.description;
			this.form.type = getSelectedValue(this.data.type, CHANNEL_TYPE_OPTIONS);
		},
		async handleSubmit() {
			this.$v.$touch();

			const isValidForm = !this.$v.$invalid;

			if (isValidForm) {
				await this.updateChannel({
					id: this.data.id,
					params: {
						name: this.form.name,
						type: this.form.type.value,
						url: this.form.link,
						description: this.form.remark,
					},
				});

				const error = this.errorChannelUpdate;

				if (error === null) {
					// show toast with success
					this.showToast({
						header: this.$t('toast.success'),
						content: this.$t('toast.updated_channel'),
						type: 'success',
					});
					// refresh channel info, request to api to get data again
					await this.fetchData();
					// set state form
					this.setState();
				} else {
					// show toast with error
					this.showToast({
						header: this.$t('toast.unable_to_save'),
						content: error.message,
						type: 'danger',
					});
				}
			}
		},
		handleCancel() {
			// redirect to channel list pages
			this.$router.push({ name: ROUTER_NAME.CHANNEL_LIST });
		},
		handleOpenModalRemove() {
			this.$refs['modal-confirm-delete'].open();
		},
		async handleConfirmRemove() {
			await this.removeChannel(this.$route.params.id);

			if (this.errorDelete === null) {
				this.isShowModalConfirmDelete = false;

				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.data_has_been_removed'),
					type: 'success',
				});

				// redirect to list pages
				this.$router.push({ name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST });
			} else {
				this.showToast({
					header: this.$t('toast.unable_to_remove'),
					content: this.errorDelete.message,
					type: 'danger',
				});
			}
		},
		handleCancelRemove() {
			this.isShowModalConfirmDelete = false;
		},
	},
};
</script>

<style lang="scss" scoped>
.container-date {
	@include typo-helper-text;

	color: $color-black-45;

	p {
		margin-bottom: rem(4);

		&:last-of-type {
			margin-bottom: 0;
		}
	}
}
</style>
